import {
  BRAND_NAME,
  PreviewRecipe,
  TAXONOMY_IDS,
  TAXONOMY_TYPES,
} from "@chef/constants";
import { isEqualStrings } from "@chef/utils/equal";
import { isNotEmptyObject } from "@chef/utils/object";
import { uniqueByProperty } from "@chef/utils/array";

import {
  OrderQuery,
  PickAndMixQuery,
  RecipeAndStepsQuery,
  RecipesByIngredientCategoriesQuery,
  RecipesByIdsQuery,
  RecipesByProductIdListQuery,
} from "../graphql/generated";

export const getRecipesFromPickAndMix = (args: {
  pickAndMix: PickAndMixQuery["pickAndMix"];
  productId: string;
}) => {
  const { pickAndMix, productId } = args;

  return (
    pickAndMix.find((pam) => isEqualStrings(pam.productId, productId))
      ?.recipes || null
  );
};

export const getRecipeFromPickAndMix = (args: {
  pickAndMix: PickAndMixQuery["pickAndMix"];
  productId: string;
  variationId: string;
}) => {
  const { pickAndMix, productId, variationId } = args;

  for (const pnm of pickAndMix) {
    if (!isEqualStrings(pnm.productId, productId)) {
      continue;
    }

    for (const recipe of pnm.recipes) {
      for (const portion of recipe.portions) {
        if (!isEqualStrings(portion.variationId, variationId)) {
          continue;
        }

        return recipe;
      }
    }
  }

  return null;
};

export const findFeaturedPortraitImage = (
  recipeData:
    | PickAndMixQuery["pickAndMix"][number]["recipes"][number]
    | RecipesByProductIdListQuery["recipesByProductIdList"][number]["products"][number]["recipes"][number]
    | OrderQuery["order"]["orderLines"][number]["recipes"][number]
    | RecipeAndStepsQuery["recipeAndSteps"]
    | RecipesByIdsQuery["recipesByIds"][number]
    | PreviewRecipe
    | RecipesByIngredientCategoriesQuery["getRecipesByIngredientCategories"]["recipes"][number],
  size?: "large" | "small",
) => {
  const images = recipeData.images;

  const _size = size || "large";

  if (!images) {
    return;
  }
  if (isNotEmptyObject(images)) {
    return (
      images.urls.find((u) => u.size === _size) ||
      images.urls.find((u) => u !== undefined)
    );
  }
  return;
};

export const generateTags = (
  item:
    | PickAndMixQuery["pickAndMix"][number]["recipes"][number]
    | RecipeAndStepsQuery["recipeAndSteps"]
    | RecipesByProductIdListQuery["recipesByProductIdList"][number]["products"][number]["recipes"][number]
    | RecipesByIngredientCategoriesQuery["getRecipesByIngredientCategories"]["recipes"][number]
    | RecipesByIdsQuery["recipesByIds"][number]
    | OrderQuery["order"]["orderLines"][number]["recipes"][number]
    | PreviewRecipe,
) => {
  const {
    taxonomies = [],
    cookingTimeMin,
    cookingTimeMax,
    shelfLife: shelfLifeDays,
  } = item;

  const marketingTax = taxonomies.find(
    (t) => t.type === TAXONOMY_TYPES.MARKETING,
  );

  const campaignTax = taxonomies.find(
    (t) => t.type === TAXONOMY_TYPES.CAMPAIGN,
  );

  const specialFood = uniqueByProperty(
    taxonomies
      .filter(
        (t) =>
          t.type === TAXONOMY_TYPES.SPECIALFOOD ||
          (t.type === TAXONOMY_TYPES.CATEGORY &&
            [
              TAXONOMY_IDS.GLUTENFREE,
              TAXONOMY_IDS.GLUTENFREE_OLD,
              TAXONOMY_IDS.LACTOSEFREE,
              TAXONOMY_IDS.LACTOSEFREE_OLD,
            ].includes(+t.taxonomyId as any)),
      )
      .map(({ name, taxonomyId }) => ({
        id: taxonomyId,
        text: name,
      })),
    "text",
  );

  const categoryTag = taxonomies
    .filter((t) => t.type === TAXONOMY_TYPES.CATEGORY)
    .map(({ name, taxonomyId }) => ({
      id: taxonomyId,
      text: name,
    }));

  const cookingTime =
    cookingTimeMax === cookingTimeMin
      ? {
          id: "cookingTime",
          text: cookingTimeMin,
        }
      : {
          id: "cookingTime",
          text: `${cookingTimeMin}-${cookingTimeMax}`,
        };

  const marketing = {
    id: marketingTax && marketingTax.name,
    text: marketingTax && marketingTax.name,
  };

  const campaign = {
    id: campaignTax && campaignTax.name,
    text: campaignTax && campaignTax.name,
  };

  let shelfLife: { id: string; text: string } | null = null;

  // Only show shelfLife on RetNemt
  if (BRAND_NAME === "RN" && shelfLifeDays) {
    shelfLife = {
      id: "shelfLife",
      text: intl.SHELF_LIFE(shelfLifeDays),
    };
  }

  return {
    cookingTime,
    marketing,
    campaign,
    specialFood,
    shelfLife,
    categoryTag,
  };
};

const intl = {
  AMK: {
    SHELF_LIFE: (days: number) => `${days} dagers holdbarhet`,
  },
  GL: {
    SHELF_LIFE: (days: number) => `${days} dagers holdbarhet`,
  },
  LMK: {
    SHELF_LIFE: (days: number) => `${days} dagars hållbarhet`,
  },
  RN: {
    SHELF_LIFE: (days: number) => `${days} dages holdbarhed`,
  },
}[BRAND_NAME];
